import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type ViewInstance } from 'react-native';

import { Portal } from '@/components/ui';

export interface GridTypeaheadOption {
	id: string;
	display: string;
	primary?: string;
	secondary?: string;
	tertiary?: string;
}

export interface GridTypeaheadProps<T> {
	items: T[];
	toOption: (item: T) => GridTypeaheadOption;
	value: string | null | undefined;
	onSelect: (item: T | null) => void;
	placeholder?: string;
	testID?: string;
	/**
	 * Show the full option list (capped at MAX_RESULTS) as soon as the cell is
	 * focused, before any typing. Opt-in — leave off for pickers with long lists
	 * (items, GL codes) where an unfiltered dump would overwhelm; turn on for
	 * small closed vocabularies (e.g. the tag filter's ~11 options).
	 */
	showAllOnEmpty?: boolean;
	/**
	 * Pin the FIRST item to the top of the dropdown, always shown regardless of
	 * the typed filter — for a synthetic action option like "NEW — create this".
	 */
	pinnedFirst?: boolean;
}

const MAX_RESULTS = 12;

/**
 * Bare-input typeahead designed to drop into a grid cell that supplies its own
 * border. Mirrors `GlCodeTypeahead`'s keyboard model so spreadsheet-style
 * journal/invoice composers feel uniform: ArrowUp/Down moves highlight, Tab
 * commits the highlighted match and lets the browser advance focus natively,
 * Enter commits without advancing, Escape closes the dropdown. The dropdown
 * renders through a Portal at fixed viewport coords so containing grids cannot
 * clip it.
 */
export const GridTypeahead = <T,>({
	items,
	toOption,
	value,
	onSelect,
	placeholder,
	testID,
	showAllOnEmpty = false,
	pinnedFirst = false,
}: GridTypeaheadProps<T>) => {
	const options = useMemo(() => items.map((it) => ({ item: it, opt: toOption(it) })), [items, toOption]);

	const [text, setText] = useState('');
	const [highlight, setHighlight] = useState(0);
	const [open, setOpen] = useState(false);
	const wrapRef = useRef<ViewInstance>(null);
	const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);

	useEffect(() => {
		if (!value) { setText(''); return; }
		const found = options.find((o) => o.opt.id === value);
		if (found && found.opt.display !== text) setText(found.opt.display);
	}, [value, options]); // eslint-disable-line react-hooks/exhaustive-deps

	const matches = useMemo(() => {
		const q = text.trim().toLowerCase();
		// The pinned first item (e.g. a "NEW — create this" action) is always shown
		// at the top, never filtered out; the rest of the list filters normally.
		const pinned = pinnedFirst && options.length ? [options[0]] : [];
		const pool = pinnedFirst ? options.slice(1) : options;
		// Selected value's display matches the query → user hasn't started a new
		// search; show only the pinned action (if any), not the whole list.
		if (value) {
			const sel = options.find((o) => o.opt.id === value);
			if (sel && sel.opt.display.toLowerCase() === q) return pinned;
		}
		// Empty input: show everything when opted in (small closed lists), else
		// nothing (the default — avoids dumping a huge item/GL list on focus).
		if (!q) return [...pinned, ...(showAllOnEmpty ? pool.slice(0, MAX_RESULTS) : [])];
		const filtered = pool.filter(({ opt }) =>
			opt.display.toLowerCase().includes(q)
			|| (opt.primary ?? '').toLowerCase().includes(q)
			|| (opt.secondary ?? '').toLowerCase().includes(q));
		return [...pinned, ...filtered.slice(0, MAX_RESULTS)];
	}, [options, text, value, showAllOnEmpty, pinnedFirst]);

	useEffect(() => { setHighlight(0); }, [text]);

	const measureAnchor = useCallback(() => {
		const node = wrapRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setAnchorRect({ top: y + h, left: x, width: w }));
		}
	}, []);

	useEffect(() => {
		if (!open) return;
		measureAnchor();
		if (Platform.OS !== 'web') return;
		const onMove = () => measureAnchor();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measureAnchor, matches.length]);

	const commit = (m: { item: T; opt: GridTypeaheadOption }) => {
		setText(m.opt.display);
		onSelect(m.item);
		setOpen(false);
	};

	return (
		<View ref={wrapRef} style={styles.wrap}>
			<TextInput
				value={text}
				onChangeText={(v) => {
					setText(v);
					setOpen(true);
					if (!v.trim() && value) onSelect(null);
				}}
				autoCorrect={false}
				autoCapitalize="none"
				placeholder={placeholder}
				placeholderTextColor="#b0bec5"
				style={styles.input}
				onFocus={() => setOpen(true)}
				onBlur={() => setTimeout(() => setOpen(false), 120)}
				testID={testID}
				onKeyPress={(e: any) => {
					const key = e.nativeEvent?.key as string | undefined;
					if (key === 'ArrowDown') {
						e.preventDefault?.();
						setHighlight((h) => Math.min(matches.length - 1, h + 1));
					} else if (key === 'ArrowUp') {
						e.preventDefault?.();
						setHighlight((h) => Math.max(0, h - 1));
					} else if (key === 'Tab') {
						const top = matches[highlight] ?? matches[0];
						if (top) commit(top);
					} else if (key === 'Enter') {
						const top = matches[highlight] ?? matches[0];
						if (top) {
							e.preventDefault?.();
							commit(top);
						}
					} else if (key === 'Escape') {
						setOpen(false);
					}
				}}
			/>
			{open && matches.length > 0 && anchorRect && (
				<Portal>
					<View
						testID={testID ? `${testID}-dropdown` : 'grid-typeahead-dropdown'}
						style={{
							position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
							top: anchorRect.top + 2,
							left: anchorRect.left,
							width: Math.max(anchorRect.width, 280),
							zIndex: 99999,
							maxHeight: 320,
						}}
					>
						<View style={styles.dropdown}>
							<ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="always" nestedScrollEnabled>
								{matches.map((m, idx) => (
									<Pressable
										key={m.opt.id}
										onPress={() => commit(m)}
										style={[styles.option, idx === highlight && styles.optionActive]}
									>
										{m.opt.primary && <Text style={styles.optPrimary}>{m.opt.primary}</Text>}
										<Text style={styles.optSecondary} numberOfLines={1}>
											{m.opt.secondary ?? m.opt.display}
										</Text>
										{m.opt.tertiary && <Text style={styles.optTertiary}>{m.opt.tertiary}</Text>}
									</Pressable>
								))}
							</ScrollView>
						</View>
					</View>
				</Portal>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	wrap: { position: 'relative', flex: 1, alignSelf: 'stretch' },
	input: {
		paddingHorizontal: 8, paddingVertical: 8,
		fontSize: 13, color: '#212121',
		backgroundColor: 'transparent', minHeight: 32,
	},
	dropdown: {
		backgroundColor: '#fff',
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 6 },
		elevation: 24,
		overflow: 'hidden',
	},
	option: {
		flexDirection: 'row', alignItems: 'center', gap: 10,
		paddingVertical: 8, paddingHorizontal: 10,
		borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#eee',
	},
	optionActive: { backgroundColor: '#e3f2fd' },
	optPrimary: { fontFamily: 'monospace', fontWeight: '600', color: '#1a237e', minWidth: 64 },
	optSecondary: { color: '#263238', fontSize: 13, fontWeight: '600', flex: 1 },
	optTertiary: { color: '#90a4ae', fontSize: 11, fontFamily: 'monospace' },
});
